import { Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Bound } from "lib/@components/binding/Bound"
import { useCurrentUser } from "lib/store/use-current-user"
import { hasDemand } from "lib/authorization/has-demand"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { NavSwitcher } from "lib/@components/nav-switcher/nav-switcher"
import { CompanyAvatar } from "./company-avatar"
import { CompanyMenuItem } from "./company-menu-item"

export function CompanySwitcher({ isCollapse }) {
    const { clients = [], selectedClient, setSelectedClient } = useBoundContext()
    const user = useCurrentUser()

    if (hasDemand("$sharing")) return null
    const selected = clients.find(isSelected)
    if (!selected) return null

    const companyName = selected.name || selected.displayName
    const availableOptions = clients.filter(isNotSelected).sort(byName)
    const canSwitch = availableOptions.length > 0

    return (
        <NavSwitcher
            id="company-switcher-menu"
            dataCy="company-switcher-trigger"
            tooltip={getTooltip()}
            accessibleLabel={getAccessibleLabel()}
            hiddenLabel={isCollapse ? getAccessibleLabel() : "Current company:"}
            menuLabel="Companies"
            canSwitch={canSwitch}
            isCollapse={isCollapse}
            face={
                <>
                    <CompanyAvatar client={selected} />
                    <If value={!isCollapse}>
                        <Stack sx={{ minWidth: 0, flex: 1, alignItems: "flex-start" }}>
                            <Typography component="span" variant="subtitle1" noWrap sx={{ maxWidth: 1 }}>
                                {companyName}
                            </Typography>
                            <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 1 }}>
                                {user.displayName}
                            </Typography>
                            <If value={!!user.jobTitle}>
                                <Typography
                                    component="span"
                                    variant="body2"
                                    noWrap
                                    sx={{ maxWidth: 1, color: "text.secondary" }}
                                >
                                    {user.jobTitle}
                                </Typography>
                            </If>
                        </Stack>
                    </If>
                </>
            }
        >
            <Bound selectCompany={setSelectedClient}>
                <Repeat list={availableOptions} item={<CompanyMenuItem />} keyFn={byId} />
            </Bound>
        </NavSwitcher>
    )

    function getTooltip() {
        if (!isCollapse) return undefined
        return canSwitch ? "Switch company" : "Company"
    }

    function getAccessibleLabel() {
        const parts = [`Current company: ${companyName}`, `Current user: ${user.displayName}`]
        if (user.jobTitle) parts.push(`Role: ${user.jobTitle}`)
        return parts.join(". ")
    }

    function isSelected(client) {
        return client.id === selectedClient
    }

    function isNotSelected(client) {
        return client.id !== selectedClient
    }

    function byName(a, b) {
        return (a.name || a.displayName || "").localeCompare(b.name || b.displayName || "")
    }

    function byId(client) {
        return client.id
    }
}
